import type { RegelFormular } from "../model/nachlauf";
import { t } from "../lib/sprache";

/**
 * Felder einer Nachlaufregel (Nachprovision nach Austritt, 0074): Dauer, Satz oder zeitliche Staffel, Geltung, laufende
 * Kooperationen. Genutzt in Einstellungen · Konditionen (Standard) und im Archiv-Dialog der Stammdaten (Regel je Creator).
 */
export function NachlaufRegelFelder({ f, set, idPrefix }: { f: RegelFormular; set: (patch: Partial<RegelFormular>) => void; idPrefix: string }) {
  const stufe = (i: number, patch: Partial<RegelFormular["stufen"][number]>) => set({ stufen: f.stufen.map((z, j) => (j === i ? { ...z, ...patch } : z)) });
  return (
    <div className="stack">
      <div className="grid">
        <label>
          {t("Dauer in Monaten", "Duration in months")}<input id={`${idPrefix}-monate`} type="number" min="1" max="60" step="1" inputMode="numeric" value={f.monate} onChange={(e) => set({ monate: e.target.value })} />
        </label>
        <label>
          {t("Provision im Nachlauf", "Post-term commission")}<select id={`${idPrefix}-art`} value={f.art} onChange={(e) => set({ art: e.target.value as RegelFormular["art"] })}>
            <option value="satz">{t("Ein Satz für die ganze Dauer", "One rate for the whole duration")}</option>
            <option value="staffel">{t("Staffel nach Monaten seit dem Austritt", "Tiers by months since leaving")}</option>
          </select>
        </label>
        {f.art === "satz" && (
          <label>
            {t("Satz in Prozent", "Rate in percent")}<input id={`${idPrefix}-satz`} inputMode="decimal" value={f.satz} onChange={(e) => set({ satz: e.target.value })} placeholder="z. B. 15" />
          </label>
        )}
      </div>
      {f.art === "staffel" && (
        <fieldset className="stack">
          <legend>{t("Staffel", "Tiers")}</legend>
          {f.stufen.map((z, i) => {
            const letzte = i === f.stufen.length - 1;
            return (
              <div key={i} className="row">
                <label>{letzte ? t("Bis zum Ende (Monat)", "To the end (month)") : t("Bis einschließlich Monat", "Up to and including month")}
                  {letzte
                    ? <input disabled value={f.monate} aria-label={t(`Stufe ${i + 1} bis zum Ende`, `Tier ${i + 1} to the end`)} />
                    : <input inputMode="numeric" value={z.bis} aria-label={t(`Stufe ${i + 1} bis Monat`, `Tier ${i + 1} up to month`)} onChange={(e) => stufe(i, { bis: e.target.value })} />}
                </label>
                <label>{t("Satz in Prozent", "Rate in percent")}<input inputMode="decimal" value={z.satz} aria-label={t(`Stufe ${i + 1} Satz`, `Tier ${i + 1} rate`)} onChange={(e) => stufe(i, { satz: e.target.value })} /></label>
                {f.stufen.length > 1 && <button type="button" onClick={() => set({ stufen: f.stufen.filter((_, j) => j !== i) })} aria-label={t(`Stufe ${i + 1} entfernen`, `Remove tier ${i + 1}`)}>{t("Entfernen", "Remove")}</button>}
              </div>
            );
          })}
          <div className="row">
            <button type="button" disabled={f.stufen.length >= 6}
              onClick={() => set({ stufen: [...f.stufen.slice(0, -1), { bis: "", satz: f.stufen.at(-1)?.satz ?? "" }, { bis: f.monate, satz: "" }] })}>
              {t("Stufe hinzufügen", "Add tier")}</button>
          </div>
        </fieldset>
      )}
      <div className="stack tk-gap-sm" role="radiogroup" aria-label={t("Für welche Kooperationen", "For which deals")}>
        <label className="row" style={{ flexWrap: "nowrap", alignItems: "flex-start", fontWeight: 400 }}>
          <input type="radio" name={`${idPrefix}-geltung`} checked={f.geltung === "concluded"} onChange={() => set({ geltung: "concluded" })}
            style={{ flex: "none", width: 20, height: 20, minHeight: 0, padding: 0, margin: "2px 0 0" }} />
          <span><strong>{t("In der Laufzeit abgeschlossene Kooperationen", "Deals closed during the term")}</strong><br /><small>{t("Nur Kooperationen, die beim Austritt schon abgeschlossen sind (nicht mehr in der Anbahnung).", "Only deals already closed when leaving (no longer in the pipeline).")}</small></span>
        </label>
        <label className="row" style={{ flexWrap: "nowrap", alignItems: "flex-start", fontWeight: 400 }}>
          <input type="radio" name={`${idPrefix}-geltung`} checked={f.geltung === "initiated"} onChange={() => set({ geltung: "initiated" })}
            style={{ flex: "none", width: 20, height: 20, minHeight: 0, padding: 0, margin: "2px 0 0" }} />
          <span><strong>{t("Auch in der Laufzeit angebahnte", "Also those initiated during the term")}</strong><br /><small>{t("Zusätzlich Kooperationen, die die Agentur angebahnt hat und die in der Nachlaufzeit zustande kommen (beim Austritt noch in der Anbahnung oder danach angelegt); sie bekommen beim Abschluss den Satz zum Abschlusstag fest.", "Additionally, deals the agency initiated that are closed during the post-term period (still in the pipeline when leaving or created afterwards); they get the rate on the closing date fixed when closed.")}</small></span>
        </label>
      </div>
      <label className="row">
        <input type="checkbox" checked={f.laufende} onChange={(e) => set({ laufende: e.target.checked })} />
        {t("Laufende Kooperationen ab dem Austritt je Monat nach der Regel abrechnen", "Bill ongoing deals monthly under the rule from leaving onwards")}</label>
      {f.laufende && (
        <label>
          {t("Satz nach Ablauf in Prozent", "Rate after expiry in percent")}<input id={`${idPrefix}-danach`} inputMode="decimal" value={f.danach} onChange={(e) => set({ danach: e.target.value })} placeholder="0" />
          <small>{t("Gilt für umgestellte laufende Kooperationen nach dem Ende der Regel; 0 = keine Agenturprovision mehr.", "Applies to converted ongoing deals after the rule ends; 0 = no more agency commission.")}</small>
        </label>
      )}
    </div>
  );
}
